import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { MultiSelect } from '@/components/ui/multi-select'
import type { WorkflowRunStatus } from '@/types'
import { ClearFiltersIcon } from '@/components/icons'
import { Search, X } from 'lucide-react'
import { workflowRunStatusFilterOptions } from '../shared'

const statusOptions = workflowRunStatusFilterOptions.filter(o => o.value !== '')

interface WorkflowDefinitionOption {
  name: string
  displayName: string
}

interface Props {
  search: string
  statuses: WorkflowRunStatus[]
  definitionName: string
  onSearchChange: (search: string) => void
  onStatusesChange: (statuses: WorkflowRunStatus[]) => void
  onDefinitionChange: (name: string) => void
  definitions: WorkflowDefinitionOption[]
  isSortDefault: boolean
  onClearAll: () => void
}

export function WorkflowRunFilters({
  search,
  statuses,
  definitionName,
  onSearchChange,
  onStatusesChange,
  onDefinitionChange,
  definitions,
  isSortDefault,
  onClearAll,
}: Props) {
  const hasActiveFilters = search !== '' || statuses.length > 0 || (definitionName !== '_all' && definitionName !== '') || !isSortDefault

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="The list is searchable by workflow name, user, work item"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9"
          aria-label="Search workflow runs"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <MultiSelect
        options={statusOptions}
        value={statuses}
        onChange={(v) => onStatusesChange(v as WorkflowRunStatus[])}
        placeholder="All Statuses"
        displayMode="compact"
        searchable={false}
        showCompactIcon={false}
        showCompactOverflowCount
        className="w-[200px]"
        aria-label="Filter by status"
      />

      <Select value={definitionName || '_all'} onValueChange={onDefinitionChange}>
        <SelectTrigger className="w-[200px]" aria-label="Filter by workflow">
          <SelectValue placeholder="All Workflows" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Workflows</SelectItem>
          {definitions.map(def => (
            <SelectItem key={def.name} value={def.name}>{def.displayName}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="ghost"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex items-center gap-1.5 text-sm font-medium"
        aria-label="Clear all filters"
      >
        <ClearFiltersIcon className="h-4 w-4" />
        Clear All
      </Button>
    </div>
  )
}
